import React, { useContext } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { CALENDAR_DRAG_TYPE, slotDroppableId, mealDraggableId } from './dragTypes';
import { calendarDropStyle } from './pointerDropTarget';
import { MealTipContext } from './MealChipTooltip';

/**
 * One labelled slot in a calendar day: Breakfast, Lunch, Dinner, Snack, or the Other row.
 *
 * The Other row is always mounted, even when it is hidden, because react-beautiful-dnd only
 * offers the droppables that exist when a drag starts. `collapsed` hides it with CSS instead.
 *
 * rbd's placeholder only takes up room in the slot a meal is dragged out of, where it holds the
 * meal's place. In the slot rbd believes it is over it would grow that slot and shift the grid
 * under the pointer, and rbd's belief is not where the meal lands anyway: pointerDropTarget.js
 * decides that, and tints the slot under the pointer in place of rbd's `isDraggingOver`.
 *
 * A hovered chip reports itself to the month grid's tooltip (MealChipTooltip).
 */
export default function MealSlot({ date, slot, meals, collapsed = false }) {
	const tip = useContext(MealTipContext);
	const classes = [
		'meal-slot',
		`meal-slot--${slot.key}`,
		meals.length === 0 ? 'is-empty' : '',
		collapsed ? 'is-collapsed' : '',
	].filter(Boolean).join(' ');

	return (
		<Droppable droppableId={slotDroppableId(date, slot.id)} type={CALENDAR_DRAG_TYPE}>
			{(provided, snapshot) => (
				<div className={classes} data-slot={slot.key}>
					<span className='meal-slot__label' title={slot.name}>{slot.shortName}</span>
					<div
						className='meal-slot__meals'
						ref={provided.innerRef}
						{...provided.droppableProps}
					>
						{meals.map((meal, index) => (
							<Draggable
								key={mealDraggableId(meal)}
								draggableId={mealDraggableId(meal)}
								index={index}
							>
								{(dragProvided, dragSnapshot) => (
									<div
										className={`meal-chip meal-chip--${slot.key}${dragSnapshot.isDragging ? ' is-dragging' : ''}`}
										ref={dragProvided.innerRef}
										{...dragProvided.draggableProps}
										{...dragProvided.dragHandleProps}
										style={calendarDropStyle(dragProvided.draggableProps.style, dragSnapshot)}
										onMouseEnter={tip ? (event) => tip.show(meal, event.currentTarget) : undefined}
										onMouseLeave={tip ? tip.hide : undefined}
									>
										{tip ? tip.name(meal) : meal.name}
									</div>
								)}
							</Draggable>
						))}
						<div className={`meal-slot__placeholder${snapshot.draggingFromThisWith ? '' : ' is-away'}`}>
							{provided.placeholder}
						</div>
					</div>
				</div>
			)}
		</Droppable>
	);
}
